import { ONBOARDING_ALLOWED_DOMAINS_MAX, ONBOARDING_APPS_MAX, ONBOARDING_OWNER_DOMAIN_MESSAGE, onboardingAppSlugSchema, onboardingDomainSchema, onboardingOwnerDomainAllowed, onboardingOwnerEmailSchema, onboardingTenantSlugSchema, signInTypeTakesClientSecret, signInViewSchema } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/org-seed/org-seed.ts
/**
* The org seed: what the CP tells a data plane about a new org, so that core-api creates the org,
* its owner invitation and its sign-in provider with no setup token (D11).
*
* Plan `2026-10-16-customer-onboarding`, workstream WS-1, decisions D11, D12 and D15, defaults F1,
* F3 and F8, ruling R-070 item 5.
*
* Who uses it:
*
* - release-engine (WS-8) builds one {@link OrgSeed} per org that an onboarding request placed, with
*   {@link orgSeedFromRecord}, and sends the sorted list in the desired-state envelope under the
*   field {@link ORG_SEED_ENVELOPE_FIELD}.
* - zion-operator (WS-10) copies the list into the data plane, for orgs 1 to N.
* - core-api (WS-9) parses each seed with {@link orgSeedSchema} and seeds the org from it.
* - provisioning-svc (WS-6) writes the client secret, once, to the data plane Secret that
*   {@link orgSeedClientSecretName} names, under the key {@link ORG_SEED_CLIENT_SECRET_KEY} (D12).
*
* A seed holds no secret. Its `provider` is the view form of the sign-in choice, which refuses a
* `clientSecret` key. The provider also carries no marker `clientSecretLater`: the marker belongs
* to the CP request, and the seed wire keeps the shape that an older data plane parses (R-070
* item 5). Every list is sorted and holds no repeat, because the envelope is etag input
* and an unstable order would change the etag with no change behind it.
*
* Every schema here is strict and has refinements and no transform.
*/
/** The version of the seed shape. A reader refuses any other value. */
const ORG_SEED_VERSION = 1;
/** The name of the desired-state envelope field that carries the list of seeds. */
const ORG_SEED_ENVELOPE_FIELD = "orgSeeds";
/** The most seeds one envelope carries. */
const ORG_SEEDS_MAX = 1024;
/** The key of the client secret inside the data plane Secret that {@link orgSeedClientSecretName} names. */
const ORG_SEED_CLIENT_SECRET_KEY = "value";
/** The prefix of the name of every org seed Secret. */
const ORG_SEED_CLIENT_SECRET_NAME_PREFIX = "zion-auth-";
/** A UUID in lower case, the form `randomUUID()` and PostgreSQL print, and a Kubernetes name accepts. */
const ORG_SEED_ORG_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/**
* Name the data plane Secret that holds the client secret of an org's sign-in provider.
*
* Callers: provisioning-svc, which writes the Secret, and core-api, which reads it. Both derive the
* name here, so the seed carries no name a writer could point at another Secret.
*
* Input: the CP org id in lower case UUID form, and a sign-in type that takes a client secret
* (`google`, `github` or `oidc`). Output: `zion-auth-<orgId>-<type>-clientsecret`, at most 66
* characters, a valid Kubernetes Secret name.
*
* Errors: a `TypeError` for an org id outside {@link ORG_SEED_ORG_ID_PATTERN}, and a `TypeError` for
* a type that takes no client secret. The message names the rule and not the value.
*/
function orgSeedClientSecretName(orgId, type) {
	if (!ORG_SEED_ORG_ID_PATTERN.test(orgId)) throw new TypeError("orgSeedClientSecretName: the org id must be a lower-case UUID");
	if (!signInTypeTakesClientSecret(type)) throw new TypeError("orgSeedClientSecretName: the sign-in type takes no client secret");
	return `${ORG_SEED_CLIENT_SECRET_NAME_PREFIX}${orgId}-${type}-clientsecret`;
}
/** True when each entry is greater than the one before, in code unit order: sorted with no repeat. */
function strictlyAscending(values) {
	return values.every((value, index) => index === 0 || values[index - 1] < value);
}
const ASCENDING_MESSAGE = "entries ascend in code unit order and appear once";
/** The message of a seed provider that carries the marker `clientSecretLater` (R-070 item 5). */
const ORG_SEED_PROVIDER_MARKER_MESSAGE = "a seed provider carries no clientSecretLater marker";
/**
* The issue message of a seed provider object: {@link ORG_SEED_PROVIDER_MARKER_MESSAGE} for an
* unknown key list that names `clientSecretLater`, else the default message of zod.
*/
function orgSeedProviderError(issue) {
	return issue.code === "unrecognized_keys" && issue.keys.includes("clientSecretLater") ? ORG_SEED_PROVIDER_MARKER_MESSAGE : void 0;
}
/** A strict copy of a sign-in view member whose shape has no key `clientSecretLater`. */
function markerFree(shape) {
	return z.strictObject(shape, { error: orgSeedProviderError });
}
const [passwordView, passkeyView, googleView, githubView, oidcView, workosView] = signInViewSchema.options;
/**
* The seed provider: the members of `signInViewSchema` with no key `clientSecretLater` in their
* shape (R-070 item 5). The marker is absent from the shape, and not only refused by a check, so the
* JSON Schema of the seed body stays the one an older data plane publishes and parses.
*/
const orgSeedProviderSchema = z.discriminatedUnion("type", [
	passwordView,
	passkeyView,
	markerFree(googleView.omit({ clientSecretLater: true }).shape),
	markerFree(githubView.omit({ clientSecretLater: true }).shape),
	markerFree(oidcView.omit({ clientSecretLater: true }).shape),
	workosView
]);
/**
* One org seed.
*
* Fields, every one required:
* - `version`: {@link ORG_SEED_VERSION}.
* - `orgId`: the CP org id, a lower-case UUID. The data plane org takes it as its id.
* - `orgSlug`: the CP tenant slug, in the grammar of the onboarding request.
* - `owner`: `{ email }`, the address core-api sends the owner invitation to.
* - `provider`: the sign-in choice in its view form, with no client secret and no marker
*   `clientSecretLater`. A provider with the marker is an issue at `provider` (default F1: the
*   narrower type, so a new reader refuses what an older reader refuses).
* - `allowedDomains`: 0 to 32 email domains, ascending, each once. core-api writes them to the
*   provider's `allowedDomains`, the list its domain gate reads.
* - `licensedApps`: 0 to 64 app slugs, ascending, each once: the apps the CP licensed for the org.
*/
const orgSeedSchema = z.strictObject({
	version: z.literal(1),
	orgId: z.string().regex(ORG_SEED_ORG_ID_PATTERN, "orgId must be a lower-case UUID"),
	orgSlug: onboardingTenantSlugSchema,
	owner: z.strictObject({ email: onboardingOwnerEmailSchema }),
	provider: orgSeedProviderSchema,
	allowedDomains: z.array(onboardingDomainSchema).max(ONBOARDING_ALLOWED_DOMAINS_MAX).refine(strictlyAscending, { message: ASCENDING_MESSAGE }),
	licensedApps: z.array(onboardingAppSlugSchema).max(ONBOARDING_APPS_MAX).refine(strictlyAscending, { message: ASCENDING_MESSAGE })
}).refine((seed) => onboardingOwnerDomainAllowed(seed.owner.email, seed.allowedDomains), {
	path: ["owner", "email"],
	message: ONBOARDING_OWNER_DOMAIN_MESSAGE,
	when: (payload) => payload.issues.length === 0
});
/**
* The list of seeds in one envelope: 0 to {@link ORG_SEEDS_MAX} seeds, ascending by `orgSlug`, no
* slug twice and no org id twice. A consumer treats an absent field and an empty list alike.
*/
const orgSeedsSchema = z.array(orgSeedSchema).max(ORG_SEEDS_MAX).superRefine((seeds, ctx) => {
	if (!strictlyAscending(seeds.map((seed) => seed.orgSlug))) ctx.addIssue({
		code: "custom",
		path: [],
		message: "seeds ascend by orgSlug and each slug appears once"
	});
	if (new Set(seeds.map((seed) => seed.orgId)).size !== seeds.length) ctx.addIssue({
		code: "custom",
		path: [],
		message: "each orgId appears once"
	});
});
/** A copy of a sign-in view without the marker `clientSecretLater`, the form a seed provider takes. */
function providerOf(view) {
	const provider = { ...view };
	if ("clientSecretLater" in provider) delete provider.clientSecretLater;
	return provider;
}
/**
* Build the seed of an org from its stored onboarding request.
*
* Caller: release-engine (WS-8). Output: a new {@link OrgSeed} with the version, the org id, the
* request's slug as `orgSlug`, its owner email, its sign-in view as `provider` without the marker
* `clientSecretLater` (R-070 item 5), and sorted copies of its allowed domains and apps. The result parses with {@link orgSeedSchema} for any org id in
* {@link ORG_SEED_ORG_ID_PATTERN}. The input is not changed. It never throws.
*/
function orgSeedFromRecord(source) {
	const { orgId, record } = source;
	return {
		version: 1,
		orgId,
		orgSlug: record.tenantSlug,
		owner: { email: record.ownerEmail },
		provider: providerOf(record.signIn),
		allowedDomains: [...record.allowedDomains].sort(),
		licensedApps: [...record.apps].sort()
	};
}
//#endregion
export { ORG_SEEDS_MAX, ORG_SEED_CLIENT_SECRET_KEY, ORG_SEED_CLIENT_SECRET_NAME_PREFIX, ORG_SEED_ENVELOPE_FIELD, ORG_SEED_ORG_ID_PATTERN, ORG_SEED_VERSION, orgSeedClientSecretName, orgSeedFromRecord, orgSeedSchema, orgSeedsSchema };

//# sourceMappingURL=org-seed.js.map